import { HandedOff } from '../services/appconfig/handed-off.model';

export interface ISettings {
  id: string;
  type: string;
  defaultSettings: DefaultSettings;
  eTag: string;
  accessCode: AccessCode;
  device: Device;
  order: Order;
  apps: Apps;
}

export interface AccessCode {
  allowAlphanumeric: boolean;
  allowSymbols: boolean;
  length: Length;
  recycleTimeout: number;
  generatedCodeLength?: number;
}

export interface Length {
  maximum: number;
  minimum: number;
}

export interface Apps {
  frodo: Frodo;
}

export interface Frodo {
  flf: Flf;
  homescreen: Homescreen;
  launchScreen: LaunchScreen;
  style: Style;
  timeouts: FrodoTimeouts;
  handedOff: HandedOff;
}

export interface Flf {
  password: string;
}

export interface Homescreen {
  availableScreens: string[];
  country: string;
  defaultScreen: string;
  enable: boolean;
}

export interface LaunchScreen {
  enable: boolean;
  frictionlessMessage: string;
  prescriptiveMessage: string;
}

export interface Style {
  icons: Icons;
  logo: string;
  primaryColor: string;
  primaryFontColor: string;
  secondaryColor: string;
  secondaryFontColor: string;
  thirdColor: string;
  thirdFontColor: string;
}

export interface Icons {
  order: string;
  phone: string;
  scan: string;
}

export interface FrodoTimeouts {
  associateTimeout: number;
  confirmation: number;
  guidedOffloadOrder: number;
  guidedOnloadOrder: number;
  loadOrder: number;
  prescriptiveLoad: number;
  cancelRemoveTimeout: number;
  removeLightsTimeout: number;
  serviceSessionTimeout: number;
  deliveryProvider: number;
}

export interface DefaultSettings {
  APIRetriesLimit: number;
  BACK_10K_HMI: number;
  FRONT_10K_HMI: number;
  JSONServerRunning: boolean;
  JSONserverUrl: string;
  actionsTimeout: number;
  citadelURLHeader: CitadelURLHeader;
  codeFormat: string;
  compartmentTimeout: number;
  deliveryExpTimeMins: number;
  deviceAPIUrl: string;
  enableCleanUpData: boolean;
  expireLoaded: boolean;
  expireSet: number;
  gandalfNotificationEnabled: boolean;
  gfunctionCache: boolean;
  limiterOptions: LimiterOptions;
  notificationInfo: NotificationInfo;
  orderedExpireSet: number;
  retryProcess: number;
  schedexpireSet: number;
  socketioUrl: SocketioURL;
  vision10kFlow: boolean;
  tradeShow: boolean;
  workingFlowId: string;
}

export interface CitadelURLHeader {
  Accept: string;
  'Content-Type': string;
}

export interface Input {
  door: Oor;
  door2pinIndex: Door2PinIndex;
  floor: Oor;
  states: States;
}

export interface Oor {
  false: string;
  true: string;
}

export interface Door2PinIndex {
  '0': { [key: string]: number[] };
}

export interface States {
  CLOSED: string;
  FILLED: string;
  OPEN: string;
  UNFILLED: string;
}

export interface Output {
  blinking: Blinking;
  colors: Colors;
  door2pinIndex: Door2PinIndex;
  latch: Latch;
  states: { [key: string]: string[] };
}

export interface Blinking {
  blinkoff: number;
  blinkon: number;
}

export interface Colors {
  black: boolean[];
  blue: boolean[];
  cyan: boolean[];
  green: boolean[];
  purple: boolean[];
  red: boolean[];
  white: boolean[];
  yellow: boolean[];
}

export interface Latch {
  closed: null;
  open: number;
}

export interface LimiterOptions {
  blockDuration: number;
  maxWrongAttempts: number;
  resetCount: number;
}

export interface NotificationInfo {
  sendgrid: Sendgrid;
  twilio: Twilio;
}

export interface Sendgrid {
  ApiKey: string;
  From: string;
  Topic: string;
}

export interface Twilio {
  AccountSID: string;
  From: string;
  MessagingServiceSid: string;
  Token: string;
}
export interface PhidgetFlowThru {
  BLUE: number[];
  CYAN: number[];
  GREEN: number[];
  MAGENTA: number[];
  RED: number[];
  WHITE: number[];
  YELLOW: number[];
}

export enum FrontDoorSensor {
  Closed = 'CLOSED',
  Open = 'OPEN',
}

export interface SocketioURL {
  socket1: string;
}

export interface MultiStates {
  LOADED_VERIFY: string[];
  LOCKED_DOWN: string[];
  VERIFIED: string[];
  VERIFY: string[];
}

export const DEVICE_TYPES = {
  NEXT_UP: 'nextUp',
  ORDER_HQ_FRONT: 'orderHQFront',
  ORDER_HQ_REAR: 'orderHQRear',
  ORDER_HQ_FLEX: 'orderHQFlex',
  ARRAY_FL: 'array2Front',
  ARRAY_RL: 'array2Rear',
} as const;

export type DeviceType = (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];

export interface Device {
  autoDeliveryClenaup: boolean;
  guidedLoadFailForward: boolean;
  imAliveInterval: number;
  legacyDoorTimeoutSec: number;
  lights: Lights;
  isActivated: boolean;
  validatePresenceSensor: boolean;
  deviceType: DeviceType;
}

export interface Lights {
  blinkFrequency: number;
  colors: { [key: string]: Color };
}

export interface Color {
  color: string;
  mode: Mode;
}

export enum Mode {
  On = 'on',
}

export interface Order {
  disableCreation: boolean;
  dwellTimeout: number;
  notification: Notification;
  number: number;
  orderedCleanupTimeout: number;
  timeouts: OrderTimeouts;
  upDoneEventTimeout: number;
}

export interface Notification {
  loadedOrderTemplate: string;
  enablePickupDriverNotification: boolean;
  smsNotifiableTimeout: number;
}

export interface Number {
  format: string;
  length: number;
}

export interface OrderTimeouts {
  dispensing: number;
  loading: number;
  reclaiming: number;
  removing: number;
}
